import React from "react";
import { Facebook, Globe, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { useI18n } from "../../hooks/useI18n";
import { useInstitutionalOrganization } from "../../hooks/useInstitutionalOrganization";
import ProjectCredits from "./ProjectCredits";

interface FooterProps {
  showContact?: boolean;
}

const Footer: React.FC<FooterProps> = ({ showContact = true }) => {
  const { text } = useI18n();
  const institutional = useInstitutionalOrganization();
  const contactInfo = institutional?.contactInfo ?? {};
  const socialMedia = institutional?.socialMedia ?? {};
  const facebook = socialMedia.facebook;
  const socialLinkClass = "inline-flex items-center gap-2 rounded-md py-2 text-sm text-primary-dark transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary";
  const cardClass = "min-w-0 rounded-lg border border-border bg-white p-4 text-primary-dark sm:p-5";

  const contactCards = (
        <div className="grid items-start gap-4 md:grid-cols-2">
          <section aria-labelledby="footer-contact-title" className={cardClass}>
            <h2 id="footer-contact-title" className="mb-3 text-base font-semibold">{text.footer.contact}</h2>
            <ul className="space-y-2 text-sm leading-relaxed text-foreground">
              {contactInfo.address && (
                <li className="flex items-start gap-3">
                  <MapPin aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />
                  <span className="min-w-0 break-words">{contactInfo.address}</span>
                </li>
              )}
              {contactInfo.phone && (
                <li className="flex items-start gap-3">
                  <Phone aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />
                  <span className="min-w-0 break-words">{contactInfo.phone}</span>
                </li>
              )}
              {contactInfo.email && (
                <li className="flex items-start gap-3">
                  <Mail aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />
                  <span className="min-w-0 break-words">{contactInfo.email}</span>
                </li>
              )}
              {socialMedia.whatsapp && socialMedia.whatsapp !== contactInfo.phone && (
                <li className="break-words">WhatsApp: {socialMedia.whatsapp}</li>
              )}
            </ul>
          </section>

          <section aria-labelledby="footer-social-title" className={cardClass}>
            <h2 id="footer-social-title" className="mb-2 text-base font-semibold">{text.footer.followUs}</h2>
            <div className="flex flex-wrap gap-x-5 gap-y-1">
              {facebook && (
                <a href={facebook} target="_blank" rel="noopener noreferrer" className={socialLinkClass}>
                  <Facebook aria-hidden="true" className="h-5 w-5 shrink-0" />
                  <span>Facebook</span>
                </a>
              )}
              {socialMedia.instagram && (
                <a href={socialMedia.instagram} target="_blank" rel="noopener noreferrer" className={socialLinkClass}>
                  <Instagram aria-hidden="true" className="h-5 w-5 shrink-0" />
                  <span>Instagram</span>
                </a>
              )}
              {socialMedia.website && (
                <a href={socialMedia.website} target="_blank" rel="noopener noreferrer" className={socialLinkClass}>
                  <Globe aria-hidden="true" className="h-5 w-5 shrink-0" />
                  <span>{text.footer.website}</span>
                </a>
              )}
            </div>
          </section>
        </div>
  );

  const copyright = <>&copy; {new Date().getFullYear()} APINATIVOS. {text.footer.rightsReserved}</>;

    return (
      <footer>
        {showContact && <div className="bg-surface-cool py-6">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">{contactCards}</div>
        </div>}
        <ProjectCredits light />
        <div className="border-t border-primary-light/30 bg-primary-dark px-4 py-4 text-white sm:px-6">
          <div className="mx-auto flex max-w-6xl items-center gap-3 sm:gap-6">
            <span aria-hidden="true" className="h-px min-w-4 flex-1 bg-white/30" />
            <p className="text-center text-xs leading-relaxed text-white/80">{copyright}</p>
            <span aria-hidden="true" className="h-px min-w-4 flex-1 bg-white/30" />
          </div>
        </div>
      </footer>
    );
};

export default Footer;
